import { Focus, User as UserIcon, X as XIcon } from "lucide-react";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../../lib/bookingStatus";

/**
 * Filter chips + focus/customer-focus hint pills.
 *
 * Feb 2026 split — extracted from TeamCalendar.jsx so filter interaction
 * logic lives in its own file. Behaviour and testids identical.
 *
 * Interaction rules (mirrored from the original inline block):
 *   • Category chips (Abwesenheiten · Aufgaben · Wochenberichte) work in
 *     FOCUS mode — click pins the view, second click resets.
 *   • Stornierungen is an INDEPENDENT additive toggle — never affects focus.
 *   • customerFocus pill sits next to focus-hint pill and can be dismissed.
 */
export const CALENDAR_KIND_FILTERS = [
  { key: "absence",    label: "Abwesenheiten",   kinds: ["absence"] },
  // Feb 2026 · User-Directive · „Aufgaben" umfasst jetzt nicht nur die
  // manuellen `task`/`note`/`maintenance`-Events, sondern auch die
  // service_task-Events aus dem Kalender-Merge (Extraservices, separate
  // Buchungen, freie Anfragen mit konkretem `date_needed`).
  { key: "task",       label: "Aufgaben",        kinds: ["task", "note", "maintenance", "service_task"] },
  { key: "weekly",     label: "Wochenberichte",  kinds: ["weekly_update"] },
  { key: "cancelled",  label: "Stornierungen",   kinds: [] },
];

export const DEFAULT_FILTERS = { absence: true, task: true, weekly: true, cancelled: false };


export default function CalendarFilters({
  filters,
  focusFilter,
  customerFocus,
  filterCounts,
  cancellationsUnread,
  onChipClick,
  setFocusFilter,
  setCustomerFocus,
}) {
  return (
    <>
      {/* --- Filter-Chips (persistent via ui_prefs) --- Feb 2026:
          Der ganze Filter-Balken ist `sticky` direkt unter dem Haupt-Header
          (via CSS-Var `--wb-header-h`), damit man auch bei langen Listen
          jederzeit die Filter umschalten kann. Ivory-Hintergrund + dezenter
          Schatten sorgen für saubere Trennung zum durchlaufenden Content. */}
      <div
        className="sticky z-30 bg-ivory -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 border-b border-navy/10"
        style={{
          top: "var(--wb-header-h, 0px)",
          boxShadow: `0 4px 10px -6px ${withAlpha(BRAND_COLORS.navy, 0.10)}`,
        }}
        data-testid="calendar-filter-sticky"
      >
      <div className="flex items-center gap-2 flex-wrap" data-testid="calendar-filter-chips">
        <span className="label-caps text-navy-dark/50 mr-1">Anzeigen:</span>
        {CALENDAR_KIND_FILTERS.map((f) => {
          const active = filters[f.key];
          const isFocused = focusFilter === f.key;
          const isDimmed = focusFilter && focusFilter !== f.key;
          const isCancelledChip = f.key === "cancelled";
          const hasUnread = isCancelledChip && cancellationsUnread > 0;
          // Blue = "in Verwendung": either default all-active state OR the
          // single chip pinned by focus mode. Anything not blue = grey/dim.
          const showBlue = isFocused || (!focusFilter && active);
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`calendar-filter-${f.key}${isFocused ? "-focus" : active ? "-on" : "-off"}`}
              onClick={onChipClick(f.key)}
              title={isFocused
                ? "Nur diese Kategorie sichtbar — klicken zum Zurücksetzen"
                : isCancelledChip
                  ? "Stornierte Termine einblenden — die rote Zahl verschwindet, sobald Sie klicken."
                  : "Nur diese Kategorie anzeigen"}
              className={`relative select-none inline-flex items-center gap-2 px-3.5 py-1.5 text-[11.5px] tracking-[0.12em] uppercase rounded-full border transition-all ${
                showBlue
                  ? "border-navy text-ivory"
                  : hasUnread
                    ? "border-red-800 text-red-800 bg-transparent hover:bg-red-50"
                    : "border-navy/25 text-navy-dark/60 bg-transparent hover:border-navy/50"
              }`}
              style={{
                ...(showBlue ? { background: BRAND_COLORS.navy } : {}),
                ...(isDimmed ? { opacity: 0.35 } : {}),
                ...(hasUnread && !showBlue ? { borderColor: STATUS_COLORS.red, color: STATUS_COLORS.red } : {}),
              }}
              aria-pressed={showBlue}
            >
              {isCancelledChip && !showBlue && (
                <XIcon
                  className="w-3 h-3"
                  strokeWidth={2.2}
                  style={{ color: hasUnread ? STATUS_COLORS.red : withAlpha(BRAND_COLORS.navy, 0.4) }}
                />
              )}
              {f.label}
              <span
                className="inline-flex items-center justify-center min-w-[20px] px-1.5 text-[10px] leading-none py-0.5 rounded-full"
                style={showBlue
                  ? { background: withAlpha(BRAND_COLORS.gold, 0.25), color: BRAND_COLORS.ivory }
                  : { background: withAlpha(BRAND_COLORS.navy, 0.06), color: BRAND_COLORS.navy }}
              >
                {filterCounts[f.key] ?? 0}
              </span>
              {hasUnread && (
                <span
                  className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[10.5px] font-bold text-ivory rounded-full shadow-md leading-none animate-pulse"
                  style={{ background: STATUS_COLORS.red, boxShadow: `0 2px 6px ${withAlpha(STATUS_COLORS.red, 0.4)}` }}
                  data-testid="calendar-cancellations-unread-badge"
                  aria-label={`${cancellationsUnread} neue Stornierungen`}
                >
                  +{cancellationsUnread}
                </span>
              )}
            </button>
          );
        })}
        {focusFilter && (
          <button
            type="button"
            onClick={() => setFocusFilter(null)}
            data-testid="calendar-focus-exit"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy border border-transparent hover:border-navy/20 rounded-full transition-colors"
            title="Alle Kategorien wieder anzeigen"
          >
            <XIcon className="w-3 h-3" strokeWidth={1.8} />
            Alle wieder anzeigen
          </button>
        )}
      </div>
      {focusFilter && (
        <div
          className="mb-4 -mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase px-3 py-1 rounded-full"
          style={{ background: withAlpha(BRAND_COLORS.navy, 0.08), color: BRAND_COLORS.navy }}
          data-testid="calendar-focus-hint"
        >
          <Focus className="w-3 h-3" strokeWidth={1.8} />
          Nur {(CALENDAR_KIND_FILTERS.find((x) => x.key === focusFilter) || {}).label} · Klick zum Zurücksetzen
        </div>
      )}
      {customerFocus && (
        <button
          type="button"
          onClick={() => setCustomerFocus(null)}
          className="mb-4 -mt-3 ml-2 inline-flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase px-3 py-1 rounded-full hover:brightness-110 transition-all"
          style={{ background: withAlpha(STATUS_COLORS.blue, 0.14), color: STATUS_COLORS.blue, border: `1px solid ${withAlpha(STATUS_COLORS.blue, 0.3)}` }}
          data-testid="calendar-customer-focus-hint"
        >
          <UserIcon className="w-3 h-3" strokeWidth={2} />
          Nur {customerFocus.name}
          <XIcon className="w-3 h-3 opacity-70" strokeWidth={2.4} />
        </button>
      )}
      </div>
    </>
  );
}
